// ==========================================
// PINCODE LOCATION SYSTEM - OPTIMIZED v2.0
// Separated from menu code for better performance
// ==========================================

(function() {
  'use strict';

  // ==========================================
  // CONFIGURATION
  // ==========================================
  const CONFIG = {
    APPS_SCRIPT_URL: 'https://script.google.com/macros/s/AKfycbyJZ0Rzp_0UkqRmoWdxaUruWu210J-gNBlIRs0YALHsBSnoKmEBK23Cd-DjBQ1vwEmEUA/exec',
    GEOAPIFY_KEY: '5654f78477d14d97b76f197e94d399a2',
    CACHE_DURATION: 3600000, // 1 hour
    FETCH_TIMEOUT: 5000, // 5 seconds
    GEO_TIMEOUT: 10000, // 10 seconds
    GEO_MAX_AGE: 300000 // 5 minutes
  };

  // ==========================================
  // STATE MANAGEMENT
  // ==========================================
  let pincodeCache = null;
  let isInitialized = false;
  let modalLoaded = false;
  let isFetchingPincodes = false;

  // ==========================================
  // UTILITY FUNCTIONS
  // ==========================================
  
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  async function fetchWithTimeout(url, options = {}, timeout = CONFIG.FETCH_TIMEOUT) {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);
    
    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      clearTimeout(id);
      return response;
    } catch (error) {
      clearTimeout(id);
      throw error;
    }
  }

  function scheduleIdleTask(callback) {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(callback);
    } else {
      setTimeout(callback, 1);
    }
  }

  function scheduleAnimationTask(callback) {
    if ('requestAnimationFrame' in window) {
      requestAnimationFrame(callback);
    } else {
      setTimeout(callback, 16);
    }
  }

  // ==========================================
  // DATA FETCHING
  // ==========================================

  async function fetchPincodesFromSheet() {
    if (isFetchingPincodes) {
      return pincodeCache;
    }

    try {
      const cached = localStorage.getItem('pincodeCache');
      const cacheTime = localStorage.getItem('pincodeCacheTime');
      
      if (cached && cacheTime) {
        const age = Date.now() - parseInt(cacheTime, 10);
        if (age < CONFIG.CACHE_DURATION) {
          console.log('✓ Using cached pincode data');
          pincodeCache = JSON.parse(cached);
          return pincodeCache;
        }
      }

      isFetchingPincodes = true;
      console.log('Fetching pincodes from Google Sheet...');
      
      const response = await fetchWithTimeout(CONFIG.APPS_SCRIPT_URL);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error('Failed to fetch pincode data');
      }

      pincodeCache = data.pincodes;
      
      scheduleIdleTask(() => {
        try {
          localStorage.setItem('pincodeCache', JSON.stringify(pincodeCache));
          localStorage.setItem('pincodeCacheTime', String(Date.now()));
          console.log(`✓ Loaded ${data.count} pincodes`);
        } catch (e) {
          console.warn('Failed to cache pincodes:', e);
        }
      });
      
      return pincodeCache;
      
    } catch (error) {
      console.warn('Pincode fetch failed, using cache:', error);
      
      const cached = localStorage.getItem('pincodeCache');
      if (cached) {
        console.log('⚠ Using stale cache as fallback');
        pincodeCache = JSON.parse(cached);
      }
      
      return pincodeCache;
    } finally {
      isFetchingPincodes = false;
    }
  }

  // ==========================================
  // PINCODE VALIDATION
  // ==========================================

  function isPincodeAllowed(pincode) {
    if (!pincodeCache) return false;
    const normalized = String(pincode).trim();
    return pincodeCache.hasOwnProperty(normalized) && pincodeCache[normalized].serviceable;
  }

  function getDetailsForPincode(pincode) {
    if (!pincodeCache) return null;
    return pincodeCache[String(pincode).trim()] || null;
  }

  // ==========================================
  // UI UPDATES
  // ==========================================

  function updateHeaderUI(location, pincode, details = null) {
    const headerPlace = document.getElementById('header-location-place');
    const headerPincode = document.getElementById('header-location-pincode');
    
    if (!headerPlace || !headerPincode) return;
    
    scheduleAnimationTask(() => {
      if (headerPlace) {
        headerPlace.textContent = location || 'Location';
      }
      
      if (headerPincode) {
        if (pincode) {
          const displayText = location && location !== "Selected Location" 
            ? `${location} - ${pincode}` 
            : pincode;
          headerPincode.textContent = displayText;
          headerPincode.style.color = 'inherit';
        } else {
          headerPincode.textContent = "Click to set";
          headerPincode.style.color = 'inherit';
        }
      }
    });
  }

  function showNotAvailableInHeader() {
    const headerPlace = document.getElementById('header-location-place');
    const headerPincode = document.getElementById('header-location-pincode');
    
    scheduleAnimationTask(() => {
      if (headerPlace) headerPlace.textContent = "Location Status";
      if (headerPincode) {
        headerPincode.textContent = "Not available at this address";
        headerPincode.style.color = '#CC0000';
      }
    });
    
    scheduleIdleTask(() => {
      localStorage.removeItem('userPincode');
      localStorage.removeItem('userLocation');
    });
  }

  function updateWidgetUI(pincode) {
    const deliveryPin = document.getElementById('delivery-pincode');
    if (deliveryPin) {
      scheduleAnimationTask(() => {
        deliveryPin.textContent = pincode;
      });
    }
  }

  function showMessage(type, pincode, details = null) {
    const message = document.getElementById('availability-message');
    if (!message) return;
    
    scheduleAnimationTask(() => {
      message.style.display = "flex";
      
      if (type === 'success') {
        Object.assign(message.style, {
          background: "#ecfdf5",
          border: "1px solid #a7f3d0",
          color: "#047857"
        });
        message.innerHTML = `✅ <strong>Available</strong><br>Delivery available at ${pincode}`;
      } else {
        Object.assign(message.style, {
          background: "#fff1f2",
          border: "1px solid #fecaca",
          color: "#991b1b"
        });
        message.innerHTML = `❌ <strong>Not Available</strong><br>We do not deliver to ${pincode}`;
      }
    });
  }

  function hideMessage() {
    const message = document.getElementById('availability-message');
    if (message) {
      scheduleAnimationTask(() => {
        message.style.display = 'none';
      });
    }
  }

  // ==========================================
  // APP STATE MANAGEMENT
  // ==========================================

  function updateAppState(pincode, location = null, details = null) {
    if (!details) {
      details = getDetailsForPincode(pincode);
    }
    
    if (!location && details?.state) {
      location = details.state;
    }
    
    updateHeaderUI(location || "Selected Location", pincode, details);
    updateWidgetUI(pincode);
    
    scheduleIdleTask(() => {
      try {
        localStorage.setItem('userPincode', pincode);
        if (location) {
          localStorage.setItem('userLocation', location);
        }
      } catch (e) {
        console.warn('Failed to save to localStorage:', e);
      }
    });
    
    const modal = document.getElementById('pincode-modal');
    if (modal) {
      showMessage('success', pincode, details);
      setTimeout(() => { 
        modal.style.display = 'none';
        hideMessage();
      }, 1500);
    }
  }

  // ==========================================
  // GEOLOCATION
  // ==========================================

  async function fetchLocationFromCoords(lat, lon) {
    try {
      const url = `https://api.geoapify.com/v1/geocode/reverse?lat=${lat}&lon=${lon}&apiKey=${CONFIG.GEOAPIFY_KEY}`;
      const response = await fetchWithTimeout(url);
      const data = await response.json();

      if (!data.features || !data.features.length) {
        throw new Error("Location not found");
      }

      const props = data.features[0].properties;
      const postal = String(props.postcode || "").trim();

      if (!postal) { 
        updateHeaderUI("Location Error", ""); 
        return; 
      }

      if (!pincodeCache) {
        await fetchPincodesFromSheet();
      }

      if (isPincodeAllowed(postal)) {
        const details = getDetailsForPincode(postal);
        updateAppState(postal, details?.state, details);
        
        const pincodeInput = document.getElementById('pincode-input');
        if (pincodeInput) {
          pincodeInput.value = postal;
        }
      } else {
        showNotAvailableInHeader(); 
        const modal = document.getElementById('pincode-modal');
        if (modal && modal.style.display !== 'none') {
          showMessage('error', postal);
        }
      }
    } catch (err) {
      console.error('Geolocation error:', err);
      updateHeaderUI("Location Error", "");
    }
  }

  function tryGeolocation() {
    if (!navigator.geolocation) {
      console.log('Geolocation not supported');
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        if (!pincodeCache) {
          await fetchPincodesFromSheet();
        }
        fetchLocationFromCoords(pos.coords.latitude, pos.coords.longitude);
      },
      (error) => {
        console.log('Geolocation denied or failed:', error.message);
      },
      { 
        timeout: CONFIG.GEO_TIMEOUT, 
        maximumAge: CONFIG.GEO_MAX_AGE,
        enableHighAccuracy: false
      }
    );
  }

  // ==========================================
  // MODAL MANAGEMENT
  // ==========================================

  function loadModal() {
    if (modalLoaded) return true;
    
    const template = document.getElementById('pincode-modal-template');
    if (!template) {
      console.error('Modal template not found');
      return false;
    }
    
    const modalHTML = template.content.cloneNode(true);
    document.body.appendChild(modalHTML);
    modalLoaded = true;
    
    setupModalListeners();
    
    return true;
  }

  function setupModalListeners() {
    const modal = document.getElementById('pincode-modal');
    const closeBtn = document.getElementById('close-modal');
    const cancelBtn = document.getElementById('cancel-btn');
    const saveBtn = document.getElementById('save-pincode-btn');
    const locationBtn = document.getElementById('use-location');
    const pincodeInput = document.getElementById('pincode-input');
    
    if (!modal) return;

    const closeModal = () => {
      modal.style.display = 'none';
      hideMessage();
    };
    
    if (closeBtn) {
      closeBtn.addEventListener('click', closeModal, { passive: true });
    }
    
    if (cancelBtn) {
      cancelBtn.addEventListener('click', closeModal, { passive: true });
    }

    if (saveBtn) {
      saveBtn.addEventListener('click', async () => {
        const pin = pincodeInput.value.trim();
        
        if (!pin || !/^\d{6}$/.test(pin)) { 
          alert('Please enter a valid 6-digit pincode'); 
          return; 
        }

        saveBtn.disabled = true;
        saveBtn.textContent = 'Checking...';

        if (!pincodeCache) {
          await fetchPincodesFromSheet();
        }

        await new Promise(resolve => setTimeout(resolve, 300));

        if (isPincodeAllowed(pin)) {
          const details = getDetailsForPincode(pin);
          updateAppState(pin, details?.state, details);
        } else {
          showNotAvailableInHeader(); 
          showMessage('error', pin);
        }
        
        saveBtn.disabled = false;
        saveBtn.textContent = 'Save';
      });
    }

    if (locationBtn) {
      locationBtn.addEventListener('click', async () => {
        if (!navigator.geolocation) { 
          alert('Geolocation is not supported by your browser'); 
          return; 
        }
        
        locationBtn.textContent = 'Locating...';
        locationBtn.disabled = true;
        
        if (!pincodeCache) {
          await fetchPincodesFromSheet();
        }
        
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            fetchLocationFromCoords(pos.coords.latitude, pos.coords.longitude)
              .finally(() => {
                locationBtn.innerHTML = '<svg width="12" height="12" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 122.88 122.88"><path d="M120.3.14,1.24,40.38A1.82,1.82,0,0,0,.1,42.7a1.78,1.78,0,0,0,1.21,1.15h0L60.85,62,79,121.58h0a1.78,1.78,0,0,0,1.15,1.21,1.82,1.82,0,0,0,2.32-1.14L122.74,2.58A1.85,1.85,0,0,0,120.3.14Z"/></svg> Use Current Location';
                locationBtn.disabled = false;
              });
          },
          (error) => { 
            let errorMsg = 'Could not get your location';
            if (error.code === 1) errorMsg = 'Location permission denied';
            else if (error.code === 2) errorMsg = 'Location unavailable';
            else if (error.code === 3) errorMsg = 'Location request timeout';
            
            alert(errorMsg); 
            locationBtn.innerHTML = '<svg width="12" height="12" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 122.88 122.88"><path d="M120.3.14,1.24,40.38A1.82,1.82,0,0,0,.1,42.7a1.78,1.78,0,0,0,1.21,1.15h0L60.85,62,79,121.58h0a1.78,1.78,0,0,0,1.15,1.21,1.82,1.82,0,0,0,2.32-1.14L122.74,2.58A1.85,1.85,0,0,0,120.3.14Z"/></svg> Use Current Location';
            locationBtn.disabled = false;
          },
          { timeout: CONFIG.GEO_TIMEOUT }
        );
      });
    }

    if (pincodeInput) {
      const checkPincode = debounce(async (pin) => {
        if (pin.length === 6 && /^\d{6}$/.test(pin)) {
          if (!pincodeCache) {
            await fetchPincodesFromSheet();
          }
          
          if (isPincodeAllowed(pin)) {
            const details = getDetailsForPincode(pin);
            showMessage('success', pin, details);
          } else {
            showMessage('error', pin);
          }
        } else {
          hideMessage();
        }
      }, 500);
      
      pincodeInput.addEventListener('input', (e) => {
        checkPincode(e.target.value.trim());
      }, { passive: true });
    }
  }

  // ==========================================
  // INITIALIZATION
  // ==========================================

  async function init() {
    if (isInitialized) return;
    isInitialized = true;

    const savedPin = localStorage.getItem('userPincode');
    const savedLocation = localStorage.getItem('userLocation');

    if (savedPin) {
      scheduleIdleTask(async () => {
        await fetchPincodesFromSheet();
        
        if (!isPincodeAllowed(savedPin)) {
          console.log('Saved pincode no longer valid');
          showNotAvailableInHeader();
        }
      });
    } else {
      scheduleIdleTask(() => {
        tryGeolocation();
      });
    }
  }

  function setupTriggers() {
    const headerTrigger = document.getElementById('header-pincode-trigger');
    const changeBtn = document.getElementById('change-pincode-btn');
    
    const openModal = async () => {
      if (!loadModal()) {
        console.error('Failed to load modal');
        return;
      }
      
      const modal = document.getElementById('pincode-modal');
      if (!modal) return;
      
      modal.style.display = 'flex';
      hideMessage();
      
      const pincodeInput = document.getElementById('pincode-input');
      if (pincodeInput) {
        const savedPin = localStorage.getItem('userPincode');
        if (savedPin) {
          pincodeInput.value = savedPin;
        }
        setTimeout(() => pincodeInput.focus(), 100);
      }
      
      if (!pincodeCache) {
        fetchPincodesFromSheet();
      }
    };
    
    if (headerTrigger) {
      headerTrigger.addEventListener('click', openModal, { passive: true });
    }
    
    if (changeBtn) {
      changeBtn.addEventListener('click', openModal, { passive: true });
    }
  }

  // ==========================================
  // BOOTSTRAP
  // ==========================================

  function bootstrap() {
    setupTriggers();
    scheduleIdleTask(() => {
      init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap, { once: true, passive: true });
  } else {
    bootstrap();
  }

})();